'use client';

import deepmerge from 'deepmerge';
import React, { createContext, useContext } from 'react';
import ClientId from '~/constants/ClientId';
import { Consumer } from '~/constants/common/consumer';
import KubernetesAppName from '~/constants/common/KubernetesAppName';
import { type Configuration, type Cookies } from '~/contracts/common';
import type { DeepPartial } from '~/types';

export type ConfigurationForUi = Pick<
  Configuration,
  | 'dataDogRecordingSampleRate'
  | 'dataDogSampleRate'
  | 'disableSeoLinks'
  | 'enableAuthLogging'
  | 'enableGeicoDiscount'
  | 'enableNewCarConfiguratorV2'
  | 'enableRootDiscount'
  | 'enableTestDriveCenterDealershipHome'
  | 'PricingConfig'
  | 'shaqvanaYoutubeIds'
  | 'shouldShowWSYS'
  | 'shouldUseTitleCasing'
  | 'showShaqvanaVideosSection'
  | 'useCatalogOEMData'
  | 'useVaultWhyBuy'
  | 'VaultWhyBuyNarrativeConfig'
>;

export type ContextType = {
  browserCookieId: string;
  clientId: ClientId;
  consumer: Consumer | undefined;
  configuration: ConfigurationForUi;
  eventCorrelationId: string;
  isAppRouter: boolean;
  kubernetesAppName: KubernetesAppName;
  referrer: string;
  removeFooter: boolean;
  removeHeader: boolean;
  serverCookies: Cookies;
  userAgent: string;
};

export type ShaqvanaYoutubeIds = {
  shaqparkinglot?: string;
  shaqsminicooper?: string;
  shaqremodeling?: string;
  shaqmeetshaqbot?: string;
};

export const defaultContext: ContextType = {
  browserCookieId: 'dummy',
  clientId: ClientId.DEFAULT,
  consumer: undefined,
  configuration: {
    enableAuthLogging: false,
    enableGeicoDiscount: false,
    enableNewCarConfiguratorV2: false,
    enableRootDiscount: false,
    enableTestDriveCenterDealershipHome: false,
    PricingConfig: {
      MaxVehiclesToPrice: 100
    },
    shaqvanaYoutubeIds: {
      shaqparkinglot: '',
      shaqsminicooper: '',
      shaqremodeling: '',
      shaqpoweredai: ''
    },
    shouldShowWSYS: false,
    shouldUseTitleCasing: false,
    showShaqvanaVideosSection: true,
    useCatalogOEMData: false,
    useVaultWhyBuy: false,
    VaultWhyBuyNarrativeConfig: {
      bucket: {
        control: '',
        treatment: ''
      }
    }
  },
  eventCorrelationId: '',
  isAppRouter: false,
  kubernetesAppName: KubernetesAppName.MerchUI,
  referrer: '',
  removeFooter: false,
  removeHeader: false,
  serverCookies: {},
  userAgent: ''
};

const StaticContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<ContextType>;

export function StaticContextProvider({ children, ...props }: OwnProps) {
  return (
    <StaticContext.Provider value={props}>{children}</StaticContext.Provider>
  );
}

StaticContextProvider.displayName = 'StaticContextProvider';

export const generateStaticContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function useConfiguration() {
  return useContext(StaticContext).configuration;
}

export function useReferrer() {
  return useContext(StaticContext).referrer;
}

export function useConsumer(): Consumer {
  return useContext(StaticContext).consumer as Consumer;
}

export function useEventCorrelationId() {
  return useContext(StaticContext).eventCorrelationId;
}

export function useIsAppRouter() {
  return useContext(StaticContext).isAppRouter;
}

export function useRemoveHeader() {
  return useContext(StaticContext).removeHeader;
}

export default StaticContext;
